/**
 * RoiEditor.js — editor visual da zona de leitura (Onda 17: retângulo ou polígono)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Mostra o último snapshot da câmera e permite delimitar a zona onde as
 * placas são aceitas, de duas formas:
 *   Retângulo: arraste sobre a imagem.
 *   Polígono:  clique para adicionar vértices; arraste um vértice para movê-lo;
 *              duplo clique em um vértice para removê-lo (mínimo de 3).
 * O polígono resolve enquadramentos em que outra pista cruza a imagem em
 * diagonal, coisa que o retângulo sempre corta de mais ou de menos.
 *
 * Props:
 *   camera   – objeto câmera completo (roi_config em string JSON ou objeto)
 *   onSave   – callback(roi) com { x1, y1, x2, y2 } ou { polygon: [[x, y], ...] } ou null
 *   onClose  – fecha o modal
 *
 * Onda 164 (15/10/2026): a imagem ficava "borrada" nas câmeras com dias de uso — o editor desenhava um
 * contorno verde quase opaco para CADA quadro com placa dos últimos 7 dias (21.790 na entrada do Ipê),
 * e um retângulo âmbar/vermelho para cada detecção recente,
 * escurecia o quadro em 45% e cobria a zona com um véu. Agora: cada placa é um ponto translúcido no
 * centro dela (o ponto que decide se está na zona; onde passam muitas, a região fica mais forte; as
 * detecções recentes, pontos âmbar/vermelhos um pouco maiores), o
 * escurecimento e o véu seguem a "Intensidade" (padrão 50%: 15% e 12%), e "Ver imagem limpa" mostra
 * a foto sem nada além do contorno fino da zona, até clicar de novo.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiCrosshair, FiSlash, FiCheck, FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../services/api';

const MIN_VERTICES = 3;
const MAX_VERTICES = 32;
const HANDLE = 9;         // meio-lado do quadrado da alça, em pixels do canvas
const CANVAS_W = 1280;
const CANVAS_H = 720;
export const INTENSIDADE_PADRAO = 50;

const r3 = v => Math.round(v * 1000) / 1000;

/**
 * Onda 164: o peso de cada marcação para uma intensidade (0 a 100) — ou nenhuma, na imagem limpa.
 * Em 100 fica perto do desenho antigo (escurecer 0,30 contra 0,45); em 0, só os contornos da zona.
 */
export function estiloDasMarcacoes(intensidade = INTENSIDADE_PADRAO, limpa = false) {
  if (limpa) return { limpa: true, escurecer: 0, veuLeitura: 0, veuObservacao: 0, ponto: 0, recente: 0, linha: 1.5, contraste: false, rotulos: false };
  const n = Number(intensidade);
  const k = Math.max(0, Math.min(100, Number.isFinite(n) ? n : INTENSIDADE_PADRAO)) / 100;
  return {
    limpa: false,
    escurecer: r3(0.30 * k),
    veuLeitura: r3(0.24 * k),
    veuObservacao: r3(0.26 * k),
    ponto: r3(0.04 + 0.26 * k),   // por ponto: onde passam muitas placas os pontos se somam
    recente: r3(0.35 + 0.55 * k),
    linha: r3(2 + 1.5 * k),
    contraste: true,
    rotulos: true,
  };
}

function parseRoi(raw) {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (_) { return null; }
}
function boundingRect(polygon) {
  const xs = polygon.map(p => p[0]); const ys = polygon.map(p => p[1]);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}
const clamp01 = v => Math.max(0, Math.min(1, v));

const RoiEditor = ({ camera, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [mode, setMode] = useState('rect');          // 'rect' | 'polygon'
  const [rect, setRect] = useState(null);            // { x1, y1, x2, y2 } em [0,1]
  const [polygon, setPolygon] = useState([]);        // [[x, y], ...] em [0,1]
  const [drawing, setDrawing] = useState(false);
  const [startPt, setStartPt] = useState(null);
  const [currentPt, setCurrentPt] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);  // vértice sendo arrastado
  const [hoverIndex, setHoverIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const movedRef = useRef(false);
  // Onda 35: duas camadas na mesma câmera. A de leitura decide quem entra; a de
  // observação cobre a pista de saída e nunca abre ticket. Só a camada ativa é
  // editada; a outra fica desenhada em tracejado, como referência.
  const [layer, setLayer] = useState('read');        // 'read' | 'observation'
  const [otherLayer, setOtherLayer] = useState({ mode: 'rect', rect: null, polygon: [] });
  // Onda 18: onde as placas aparecem (leituras válidas dos últimos dias e detecções recentes fora da zona)
  const [showPositions, setShowPositions] = useState(true);
  const [positions, setPositions] = useState(null);   // { valid: [...], recent: [...] }
  // Onda 164: só desta abertura do editor — ao reabrir, as marcações voltam ao padrão.
  const [imagemLimpa, setImagemLimpa] = useState(false);
  const [intensidade, setIntensidade] = useState(INTENSIDADE_PADRAO);
  const estilo = estiloDasMarcacoes(intensidade, imagemLimpa);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/api/cameras/${camera.id}/plate-positions?days=7`);
        if (!cancelled) setPositions(r.data);
      } catch (_) { if (!cancelled) setPositions({ valid: [], recent: [] }); }
    })();
    return () => { cancelled = true; };
  }, [camera.id]);

  const snapshotUrl = React.useMemo(
    () => `/api/monitoring/snapshot/${camera.id}?token=${encodeURIComponent(localStorage.getItem('token') || '')}&t=${Date.now()}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [camera.id]
  );

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; setImgLoaded(true); };
    img.onerror = () => setImgError(true);
    img.src = snapshotUrl;
    return () => { img.onload = null; img.onerror = null; };
  }, [snapshotUrl]);

  // Estado inicial a partir do valor salvo
  useEffect(() => {
    const saved = parseRoi(camera.roi_config);
    if (saved && Array.isArray(saved.polygon) && saved.polygon.length >= MIN_VERTICES) {
      setMode('polygon'); setPolygon(saved.polygon.map(p => [Number(p[0]), Number(p[1])])); setRect(null);
    } else if (saved && [saved.x1, saved.y1, saved.x2, saved.y2].every(v => typeof v === 'number')) {
      setMode('rect'); setRect({ x1: saved.x1, y1: saved.y1, x2: saved.x2, y2: saved.y2 }); setPolygon([]);
    }
    // Onda 35: a zona de observação salva vira a camada inativa.
    const obs = saved && saved.observation;
    if (obs && Array.isArray(obs.polygon) && obs.polygon.length >= MIN_VERTICES) {
      setOtherLayer({ mode: 'polygon', rect: null, polygon: obs.polygon.map(p => [Number(p[0]), Number(p[1])]) });
    } else if (obs && [obs.x1, obs.y1, obs.x2, obs.y2].every(v => typeof v === 'number')) {
      setOtherLayer({ mode: 'rect', rect: { x1: obs.x1, y1: obs.y1, x2: obs.x2, y2: obs.y2 }, polygon: [] });
    } else {
      setOtherLayer({ mode: 'rect', rect: null, polygon: [] });
    }
    setLayer('read');
  }, [camera.roi_config]);

  const hasZone = mode === 'rect' ? !!rect : polygon.length >= MIN_VERTICES;

  // Onda 164: os pontos das placas numa camada própria, refeita só quando mudam os dados ou o peso
  // do ponto — o redesenho a cada movimento do mouse só cola a camada pronta (milhares de pontos).
  const camadaDePontos = React.useMemo(() => {
    const lista = (positions && positions.valid) || [];
    if (!imgLoaded || !lista.length || !(estilo.ponto > 0)) return null;
    const c = document.createElement('canvas');
    c.width = CANVAS_W; c.height = CANVAS_H;
    const g = c.getContext && c.getContext('2d');
    if (!g) return null;
    g.fillStyle = `rgba(34, 197, 94, ${estilo.ponto})`;
    for (const d of lista) {
      const cx = ((d.x1 + d.x2) / 2) * CANVAS_W; const cy = ((d.y1 + d.y2) / 2) * CANVAS_H;
      g.fillRect(cx - 2.5, cy - 2.5, 5, 5);
    }
    return c;
  }, [positions, imgLoaded, estilo.ponto]);

  // ── desenho ────────────────────────────────────────────────────────────────
  const redraw = useCallback(() => {
    const canvas = canvasRef.current; const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const ctx = canvas.getContext('2d'); const W = canvas.width; const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);

    // Onda 18: posições das placas. Verde = placa lida nos últimos 7 dias (desde a Onda 164, um ponto
    // translúcido no centro dela, da camada pronta); vermelho = detecção recente fora da zona; âmbar =
    // detecção recente dentro da zona — também como ponto (eram retângulos: centenas, na 164 em
    // produção, voltaram a cobrir a cena), um pouco maior que o verde para se distinguir.
    if (showPositions && positions && !estilo.limpa) {
      if (camadaDePontos) ctx.drawImage(camadaDePontos, 0, 0, W, H);
      const seen = new Set();
      for (const d of positions.recent || []) {
        const key = `${d.plate}:${Math.round(d.x1 * 200)}:${Math.round(d.y1 * 200)}`;
        if (seen.has(key)) continue; seen.add(key);
        ctx.fillStyle = d.inside === false ? `rgba(239, 68, 68, ${estilo.recente})` : `rgba(245, 158, 11, ${estilo.recente})`;
        ctx.fillRect(((d.x1 + d.x2) / 2) * W - 3.5, ((d.y1 + d.y2) / 2) * H - 3.5, 7, 7);
      }
    }

    // ── Onda 35d: as duas zonas visíveis ao mesmo tempo ──────────────────────
    // Antes, o editor escurecia tudo fora da zona ativa e desenhava só o
    // contorno da outra. Dava para ver o limite de uma, não a área de cada uma.
    // Agora o quadro inteiro é escurecido por igual e cada zona recebe o próprio
    // preenchimento: ciano para a de leitura, magenta para a de observação. Onde
    // elas se sobrepõem as cores se somam, que é o sinal de que o operador
    // precisa para alinhar os limites.
    const zonaAtiva = { mode, rect, polygon };
    const emDesenho = mode === 'rect' && drawing && startPt && currentPt
      ? normalizeRect(startPt, currentPt, W, H) : null;
    if (emDesenho) zonaAtiva.rect = emDesenho;

    const temZona = (z) => !!z && (z.mode === 'polygon'
      ? (z.polygon || []).length >= MIN_VERTICES
      : !!z.rect);

    const caminho = (z) => {
      ctx.beginPath();
      if (z.mode === 'polygon') {
        z.polygon.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x * W, y * H) : ctx.lineTo(x * W, y * H)));
      } else {
        const r = z.rect;
        ctx.rect(r.x1 * W, r.y1 * H, (r.x2 - r.x1) * W, (r.y2 - r.y1) * H);
      }
      ctx.closePath();
    };

    const pintarZona = (z, { fill, stroke, rotulo, ativa }) => {
      if (!temZona(z)) return;
      ctx.save();
      if (fill) { caminho(z); ctx.fillStyle = fill; ctx.fill(); }
      ctx.setLineDash(ativa ? [] : [14, 8]);
      // Onda 164: a linha segue a intensidade; o contorno escuro por baixo (contraste) some na imagem limpa.
      const linha = ativa ? estilo.linha : estilo.linha + 1;
      if (estilo.contraste) { caminho(z); ctx.strokeStyle = 'rgba(8, 10, 20, 0.85)'; ctx.lineWidth = linha + 3; ctx.stroke(); }
      caminho(z); ctx.strokeStyle = stroke; ctx.lineWidth = linha; ctx.stroke();
      ctx.setLineDash([]);
      if (!estilo.rotulos) { ctx.restore(); return; }

      const base = z.mode === 'polygon' ? boundingRect(z.polygon) : z.rect;
      const texto = ativa ? `${rotulo} (editando)` : rotulo;
      ctx.font = '700 14px system-ui, sans-serif';
      const larg = ctx.measureText(texto).width;
      const tx = Math.min(base.x1 * W + 8, W - larg - 16);
      const ty = base.y1 * H > 26 ? base.y1 * H - 10 : base.y1 * H + 22;
      ctx.fillStyle = 'rgba(8, 10, 20, 0.9)';
      ctx.fillRect(tx - 6, ty - 16, larg + 12, 22);
      ctx.fillStyle = stroke;
      ctx.fillText(texto, tx, ty);
      ctx.restore();
    };

    // Escurece o quadro inteiro por igual: o que distingue cada zona passa a ser
    // a cor do preenchimento, não o brilho.
    // Onda 164: o quanto escurece segue a intensidade (era 45% fixo); a imagem limpa não escurece.
    if (estilo.escurecer > 0) {
      ctx.fillStyle = `rgba(0, 0, 0, ${estilo.escurecer})`;
      ctx.fillRect(0, 0, W, H);
    }

    const zonaLeitura = layer === 'read' ? zonaAtiva : otherLayer;
    const zonaObservacao = layer === 'read' ? otherLayer : zonaAtiva;
    // A zona em edição é pintada por último, para ficar por cima na sobreposição.
    const ordem = layer === 'read'
      ? [[zonaObservacao, 'observation'], [zonaLeitura, 'read']]
      : [[zonaLeitura, 'read'], [zonaObservacao, 'observation']];
    for (const [z, qual] of ordem) {
      pintarZona(z, qual === 'read'
        ? { fill: estilo.veuLeitura > 0 ? `rgba(34, 211, 238, ${estilo.veuLeitura})` : null, stroke: '#22d3ee', rotulo: 'ZONA DE LEITURA', ativa: layer === 'read' }
        : { fill: estilo.veuObservacao > 0 ? `rgba(232, 121, 249, ${estilo.veuObservacao})` : null, stroke: '#e879f9', rotulo: 'ZONA DE OBSERVAÇÃO', ativa: layer === 'observation' });
    }

    // Retângulo sendo arrastado, antes de virar zona
    if (mode === 'rect' && drawing && startPt && currentPt && !temZona(zonaAtiva)) {
      ctx.strokeStyle = '#facc15'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
      ctx.strokeRect(startPt.x, startPt.y, currentPt.x - startPt.x, currentPt.y - startPt.y);
      ctx.setLineDash([]);
    }

    // Legenda fixa: diz o que cada cor cobre, sem depender de achar o rótulo (fora da imagem limpa).
    const legenda = estilo.limpa ? [] : [
      ['#22d3ee', 'Zona de leitura: aceita a placa e abre o ticket'],
      ['#e879f9', 'Zona de observação: reconhece a saída, não abre ticket'],
    ];
    if (legenda.length) {
    ctx.save();
    ctx.font = '600 13px system-ui, sans-serif';
    const largura = Math.max(...legenda.map(([, t]) => ctx.measureText(t).width)) + 42;
    ctx.fillStyle = 'rgba(8, 10, 20, 0.82)';
    ctx.fillRect(12, H - 62, largura, 50);
    legenda.forEach(([cor, texto], i) => {
      const y = H - 42 + i * 22;
      ctx.fillStyle = cor; ctx.fillRect(22, y - 10, 16, 12);
      ctx.fillStyle = '#e2e8f0'; ctx.fillText(texto, 46, y);
    });
    ctx.restore();
    }

    // Vértices do polígono ativo ficam acima de tudo: são o que se arrasta.
    if (mode === 'polygon') {
      const pts = polygon.map(([x, y]) => [x * W, y * H]);
      if (pts.length > 0 && pts.length < MIN_VERTICES) {
        ctx.strokeStyle = '#facc15'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
        ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.stroke(); ctx.setLineDash([]);
      }
      pts.forEach(([px, py], i) => {
        ctx.fillStyle = i === hoverIndex || i === dragIndex ? '#facc15' : '#22d3ee';
        ctx.fillRect(px - HANDLE, py - HANDLE, HANDLE * 2, HANDLE * 2);
        ctx.fillStyle = '#0f172a'; ctx.font = 'bold 11px monospace';
        ctx.fillText(String(i + 1), px - 3, py + 4);
      });
    } else if (temZona(zonaAtiva)) {
      const r = zonaAtiva.rect;
      ctx.fillStyle = '#22d3ee';
      [[r.x1, r.y1], [r.x2, r.y1], [r.x1, r.y2], [r.x2, r.y2]]
        .forEach(([hx, hy]) => ctx.fillRect(hx * W - 4, hy * H - 4, 8, 8));
    }
  }, [mode, rect, polygon, drawing, startPt, currentPt, hoverIndex, dragIndex, imgLoaded, showPositions, positions, otherLayer, layer, camadaDePontos, estilo.limpa, estilo.escurecer, estilo.veuLeitura, estilo.veuObservacao, estilo.recente, estilo.linha, estilo.contraste, estilo.rotulos]);

  useEffect(() => { redraw(); }, [redraw]);

  // O rótulo de cada zona passou a ser desenhado dentro de pintarZona (Onda 35d),
  // com a cor da própria zona; a versão fixa em ciano saiu junto.

  // ── utilitários de coordenadas ─────────────────────────────────────────────
  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current; const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvas.width / r.width), y: (e.clientY - r.top) * (canvas.height / r.height) };
  };
  const normalizeRect = (p1, p2, W, H) => ({
    x1: Math.min(p1.x, p2.x) / W, y1: Math.min(p1.y, p2.y) / H, x2: Math.max(p1.x, p2.x) / W, y2: Math.max(p1.y, p2.y) / H,
  });
  const vertexAt = (pt) => polygon.findIndex(([x, y]) => Math.abs(x * CANVAS_W - pt.x) <= HANDLE + 2 && Math.abs(y * CANVAS_H - pt.y) <= HANDLE + 2);

  // ── eventos ────────────────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (!imgLoaded) return;
    const pt = getCanvasPoint(e);
    if (mode === 'rect') {
      setDrawing(true); setRect(null); setStartPt(pt); setCurrentPt(pt);
      return;
    }
    const idx = vertexAt(pt);
    movedRef.current = false;
    if (idx >= 0) { setDragIndex(idx); return; }
  };
  const handleMouseMove = (e) => {
    const pt = getCanvasPoint(e);
    if (mode === 'rect') { if (drawing) setCurrentPt(pt); return; }
    if (dragIndex !== null) {
      movedRef.current = true;
      setPolygon(prev => prev.map((p, i) => (i === dragIndex ? [clamp01(pt.x / CANVAS_W), clamp01(pt.y / CANVAS_H)] : p)));
      return;
    }
    setHoverIndex(vertexAt(pt) >= 0 ? vertexAt(pt) : null);
  };
  const handleMouseUp = (e) => {
    const pt = getCanvasPoint(e);
    if (mode === 'rect') {
      if (!drawing || !startPt) return;
      setDrawing(false);
      if (Math.abs(pt.x - startPt.x) < CANVAS_W * 0.02 || Math.abs(pt.y - startPt.y) < CANVAS_H * 0.02) { setStartPt(null); setCurrentPt(null); return; }
      const r = normalizeRect(startPt, pt, CANVAS_W, CANVAS_H);
      setRect({ x1: clamp01(r.x1), y1: clamp01(r.y1), x2: clamp01(r.x2), y2: clamp01(r.y2) });
      setStartPt(null); setCurrentPt(null);
      return;
    }
    if (dragIndex !== null) { setDragIndex(null); return; }
    // clique simples fora de um vértice: adiciona vértice
    if (polygon.length < MAX_VERTICES && vertexAt(pt) < 0) {
      setPolygon(prev => [...prev, [clamp01(pt.x / CANVAS_W), clamp01(pt.y / CANVAS_H)]]);
    }
  };
  const handleDoubleClick = (e) => {
    if (mode !== 'polygon') return;
    const idx = vertexAt(getCanvasPoint(e));
    if (idx >= 0) setPolygon(prev => prev.filter((_, i) => i !== idx));
  };
  const handleMouseLeave = () => {
    if (mode === 'rect' && drawing && startPt && currentPt) {
      // encerra o retângulo no último ponto conhecido
      setDrawing(false);
      if (Math.abs(currentPt.x - startPt.x) >= CANVAS_W * 0.02 && Math.abs(currentPt.y - startPt.y) >= CANVAS_H * 0.02) {
        const r = normalizeRect(startPt, currentPt, CANVAS_W, CANVAS_H);
        setRect({ x1: clamp01(r.x1), y1: clamp01(r.y1), x2: clamp01(r.x2), y2: clamp01(r.y2) });
      }
      setStartPt(null); setCurrentPt(null);
    }
    setDragIndex(null); setHoverIndex(null);
  };

  const switchMode = (next) => {
    if (next === mode) return;
    if (next === 'polygon' && rect && polygon.length === 0) {
      // converte o retângulo atual em polígono de 4 vértices para o operador ajustar
      setPolygon([[rect.x1, rect.y1], [rect.x2, rect.y1], [rect.x2, rect.y2], [rect.x1, rect.y2]]);
    }
    if (next === 'rect' && polygon.length >= MIN_VERTICES && !rect) setRect(boundingRect(polygon));
    setMode(next); setDrawing(false); setStartPt(null); setCurrentPt(null); setDragIndex(null);
  };
  const switchLayer = (next) => {
    if (next === layer) return;
    const atual = { mode, rect, polygon };
    setMode(otherLayer.mode || 'rect');
    setRect(otherLayer.rect || null);
    setPolygon(otherLayer.polygon || []);
    setOtherLayer(atual);
    setLayer(next);
    setDrawing(false); setStartPt(null); setCurrentPt(null); setDragIndex(null); setHoverIndex(null);
  };

  const handleClear = () => { setRect(null); setPolygon([]); setStartPt(null); setCurrentPt(null); };

  const zoneOf = (m, r, poly) => {
    if (m === 'polygon') return (poly || []).length >= MIN_VERTICES ? { polygon: poly } : null;
    return r || null;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const atual = zoneOf(mode, rect, polygon);
      const outra = zoneOf(otherLayer.mode, otherLayer.rect, otherLayer.polygon);
      const leitura = layer === 'read' ? atual : outra;
      const observacao = layer === 'read' ? outra : atual;
      // A zona de observação viaja dentro da de leitura, em roi_config.observation.
      await onSave(leitura ? { ...leitura, observation: observacao || undefined } : null);
    } finally { setSaving(false); }
  };

  const bbox = mode === 'polygon' && polygon.length >= MIN_VERTICES ? boundingRect(polygon) : rect;

  return (
    <div className="roi-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="roi-modal">
        <div className="roi-modal-header">
          <div>
            <h2><FiCrosshair style={{ marginRight: 8 }} />Zonas da câmera — {camera.name}</h2>
            <p className="roi-modal-subtitle">
              {layer === 'observation'
                ? 'Zona de observação: cubra a pista de saída que esta câmera enxerga. A placa lida aqui nunca abre ticket; serve para reconhecer o veículo que está saindo, inclusive o que saiu colado em outro.'
                : (mode === 'rect'
                  ? 'Zona de leitura: arraste sobre a imagem para delimitar onde as placas de entrada são aceitas.'
                  : 'Zona de leitura: clique para adicionar vértices, arraste um vértice para movê-lo e dê duplo clique para removê-lo. Use o polígono quando outra pista cruza a imagem em diagonal.')}
            </p>
          </div>
          <button className="roi-close-btn" onClick={onClose} title="Fechar"><FiX /></button>
        </div>

        <div className="roi-mode-toggle" role="tablist">
          <button className={`roi-mode-btn ${layer === 'read' ? 'active' : ''}`} onClick={() => switchLayer('read')} disabled={saving}
            title="Zona que aceita a placa e abre o ticket de entrada">Zona de leitura</button>
          <button className={`roi-mode-btn ${layer === 'observation' ? 'active' : ''}`} onClick={() => switchLayer('observation')} disabled={saving}
            title="Pista de saída vista por esta câmera: reconhece quem está saindo e nunca abre ticket">Zona de observação</button>
          <span className="roi-mode-sep" />
          <button className={`roi-mode-btn ${mode === 'rect' ? 'active' : ''}`} onClick={() => switchMode('rect')} disabled={saving}>Retângulo</button>
          <button className={`roi-mode-btn ${mode === 'polygon' ? 'active' : ''}`} onClick={() => switchMode('polygon')} disabled={saving}>Polígono</button>
          {mode === 'polygon' && <span className="roi-mode-hint">{polygon.length} vértice(s){polygon.length < MIN_VERTICES ? ` — mínimo ${MIN_VERTICES}` : ''}</span>}
          <span style={{ flex: 1 }} />
          <label className="roi-mode-hint" title="Verde: um ponto no centro de cada placa lida nos últimos 7 dias — onde passam muitas, a região fica mais forte. Vermelho: detecções recentes fora da zona. Âmbar: detecções recentes dentro da zona.">
            <input type="checkbox" checked={showPositions} onChange={e => setShowPositions(e.target.checked)} disabled={imagemLimpa} />
            {' '}mostrar onde as placas aparecem
            {positions && <> ({(positions.valid || []).length} válidas, {(positions.recent || []).filter(d => d.inside === false).length} fora da zona)</>}
          </label>
          <label className="roi-mode-hint roi-intensidade" title="Quanto as marcações aparecem sobre a imagem: escurecimento, véu da zona e pontos das placas.">
            Intensidade{' '}
            <input type="range" min="0" max="100" step="5" value={intensidade} disabled={imagemLimpa}
              onChange={e => setIntensidade(Number(e.target.value))} aria-label="Intensidade das marcações" />
          </label>
          <button type="button" className={`roi-mode-btn ${imagemLimpa ? 'active' : ''}`} aria-pressed={imagemLimpa}
            onClick={() => setImagemLimpa(v => !v)}
            title="Mostra a foto da câmera sem marcações, só com o contorno fino da zona. Clique de novo para voltar.">
            {imagemLimpa ? <><FiEye /> Mostrar marcações</> : <><FiEyeOff /> Ver imagem limpa</>}
          </button>
        </div>

        <div className="roi-canvas-wrapper">
          {imgError ? (
            <div className="roi-no-snapshot">
              <FiSlash size={40} />
              <p>Snapshot não disponível ainda.</p>
              <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Aguarde a câmera capturar o primeiro frame e tente novamente.</p>
            </div>
          ) : (
            <>
              <canvas
                ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
                className={`roi-canvas ${imgLoaded ? 'roi-canvas-ready' : 'roi-canvas-loading'} ${mode === 'polygon' ? 'roi-canvas-polygon' : ''}`}
                onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave} onDoubleClick={handleDoubleClick}
                title={mode === 'rect' ? 'Arraste para definir a zona de leitura' : 'Clique para adicionar vértices'}
              />
              {!imgLoaded && (
                <div className="roi-canvas-spinner"><div className="spinner" /><span>Carregando imagem da câmera...</span></div>
              )}
            </>
          )}
        </div>

        {bbox && hasZone && (
          <div className="roi-coords-bar">
            <span className="roi-coords-label">{mode === 'polygon' ? `Polígono com ${polygon.length} vértices, envolvente:` : 'Zona configurada:'}</span>
            <span className="roi-coords-value">
              X: {(bbox.x1 * 100).toFixed(0)}%–{(bbox.x2 * 100).toFixed(0)}%&nbsp;|&nbsp;Y: {(bbox.y1 * 100).toFixed(0)}%–{(bbox.y2 * 100).toFixed(0)}%
            </span>
          </div>
        )}
        {!hasZone && imgLoaded && (
          <div className="roi-coords-bar roi-coords-empty">
            <span>{mode === 'polygon' && polygon.length > 0 ? 'Adicione mais vértices para fechar a zona' : 'Sem zona configurada — toda a imagem será processada'}</span>
          </div>
        )}

        <div className="roi-modal-actions">
          <button className="btn btn-outline-danger" onClick={handleClear} disabled={(!rect && polygon.length === 0) || saving} title="Remover zona e aceitar toda a imagem">
            <FiSlash /> Remover Zona
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (mode === 'polygon' && polygon.length > 0 && polygon.length < MIN_VERTICES)}>
            {saving ? 'Salvando...' : <><FiCheck /> Salvar Zona</>}
          </button>
        </div>

        <div className="roi-legend">
          <span className="roi-legend-item roi-legend-inside">Zona aceita</span>
          <span className="roi-legend-item roi-legend-outside">Zona ignorada</span>
          <span className="roi-legend-item roi-legend-tip">
            Dica: a placa é aceita quando o centro dela cai dentro da zona; exclua a rua e a pista de saída
          </span>
        </div>
      </div>
    </div>
  );
};

export default RoiEditor;
